import { getModelTypeDisplayLabel, ModelType } from "../store/global-types/customization-types";
import { BackendValidationError, ConcurrentSaveError, ConcurrentSaveErrorWrapper, getApiErrorMessage, isBackendValidationError } from "../util/errors";

/** Expected "message" value in a successful backend save response. */
export const SAVE_RESPONSE_OK_MESSAGE = 'OK';

/** Result from a customization save API call. */
export type SaveResult = {
    success: boolean;
    updatedTimestamps?: Record<string, number>;
};

/** Parse a backend save response. Handles new JSON format and legacy "OK" string/boolean. */
export function parseSaveResponse(response: unknown): SaveResult {
    // New format: { message: "OK", t_modified_u: { modelName: timestamp, ... } }
    if (response !== null && typeof response === 'object' && !Array.isArray(response)) {
        const obj = response as Record<string, unknown>;
        if (obj.message === SAVE_RESPONSE_OK_MESSAGE) {
            const updatedTimestamps = typeof obj.t_modified_u === 'object' && obj.t_modified_u !== null
                ? obj.t_modified_u as Record<string, number>
                : undefined;
            return { success: true, updatedTimestamps };
        }
    }

    // Legacy format: string "OK"
    if (response === SAVE_RESPONSE_OK_MESSAGE) {
        return { success: true };
    }

    // Legacy format: boolean true
    if (response === true) {
        return { success: true };
    }

    // Anything else is unexpected
    console.warn('Unexpected save response format:', response);
    return { success: false };
}

/**
 * Builds the result object for an onFinish handler of a save saga.
 * Extracts the duplicated concurrent-save / error / success logic shared by all model types.
 */
export function buildSaveFinishResult(
    result: SaveResult | undefined,
    error: unknown,
    modelType: ModelType
): {
    success: boolean;
    errorMessage: string | undefined;
    validationError: BackendValidationError | undefined;
    concurrentSaveError: ConcurrentSaveError | undefined;
    updatedTimestamps: SaveResult['updatedTimestamps'] | undefined;
} {
    const success = result?.success || false;
    const concurrentSaveError = error instanceof ConcurrentSaveErrorWrapper
        ? error.concurrentSaveError : undefined;

    let errorMessage: string | null = null;
    if (concurrentSaveError) {
        // don't set a generic error message for concurrent save errors -- use the dedicated state instead
    } else if (error !== null) {
        console.error(`Was not able to save ${getModelTypeDisplayLabel(modelType)} model customization`);
        errorMessage = getApiErrorMessage(error);
        console.error(errorMessage);
    } else if (!success) {
        errorMessage = `Unable to save ${getModelTypeDisplayLabel(modelType)} model customization. There might be a problem with the server.`;
    }

    return {
        success,
        errorMessage: errorMessage || undefined,
        validationError: isBackendValidationError(error) ? error : undefined,
        concurrentSaveError,
        updatedTimestamps: result?.updatedTimestamps,
    };
}
